import React, { useCallback, useMemo, useState } from "react";
import { FiX, FiTrash2, FiEdit2, FiCheck } from "react-icons/fi";
import {
  CATEGORY_COLOR_PRESETS,
  DEFAULT_CATEGORY_COLOR,
  addConferenceCategory,
  categoryNameKey,
  deleteConferenceCategory,
  normalizeCategoryColor,
  normalizeCategoryName,
  updateConferenceCategory,
} from "./conferenceCategories";
import "./ConferenceCategoryManager.css";

function sameColor(a, b) {
  return normalizeCategoryColor(a) === normalizeCategoryColor(b);
}

/** Preset swatches plus a native picker for anything outside the palette. */
function ColorField({ value, onChange, disabled }) {
  return (
    <div className="ccm__colors">
      {CATEGORY_COLOR_PRESETS.map((preset) => (
        <button
          key={preset}
          type="button"
          className={`ccm__swatch${sameColor(preset, value) ? " is-active" : ""}`}
          style={{ background: preset }}
          onClick={() => onChange(preset)}
          disabled={disabled}
          aria-label={`Use colour ${preset}`}
        />
      ))}
      <label className="ccm__custom" title="Pick a custom colour">
        <input
          type="color"
          value={normalizeCategoryColor(value)}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <span>Custom</span>
      </label>
    </div>
  );
}

/**
 * Admin-only editor for the global conference note categories. Categories arrive
 * via the tab's live subscription, so edits land without a refetch.
 */
export default function ConferenceCategoryManager({ categories, loadError, onClose }) {
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(CATEGORY_COLOR_PRESETS[7]);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState(DEFAULT_CATEGORY_COLOR);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rows = useMemo(
    () => [...(categories || [])].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))),
    [categories]
  );

  const nameTaken = useCallback(
    (name, ignoreId) =>
      rows.some(
        (c) => c.id !== ignoreId && categoryNameKey(c.name) === categoryNameKey(name)
      ),
    [rows]
  );

  const handleAdd = async (e) => {
    e.preventDefault();
    const clean = normalizeCategoryName(newName);
    if (!clean) {
      setError("Enter a category name.");
      return;
    }
    if (nameTaken(clean)) {
      setError(`"${clean}" already exists.`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await addConferenceCategory({ name: clean, color: newColor });
      setNewName("");
      setNewColor(CATEGORY_COLOR_PRESETS[7]);
    } catch (err) {
      setError(err?.message || "Could not add the category.");
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setEditName(c.name || "");
    setEditColor(normalizeCategoryColor(c.color));
    setError("");
  };

  const saveEdit = async (c) => {
    const clean = normalizeCategoryName(editName);
    if (!clean) {
      setError("Enter a category name.");
      return;
    }
    if (nameTaken(clean, c.id)) {
      setError(`"${clean}" already exists.`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await updateConferenceCategory(c.id, { name: clean, color: editColor });
      setEditingId(null);
    } catch (err) {
      setError(err?.message || "Could not save the category.");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (c) => {
    const ok = window.confirm(
      `Delete the "${c.name}" category?\n\nNotes tagged with it will keep their text but show no category tag.`
    );
    if (!ok) return;
    setBusy(true);
    setError("");
    try {
      await deleteConferenceCategory(c.id);
    } catch (err) {
      setError(err?.message || "Could not delete the category.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ccm__backdrop" onMouseDown={onClose}>
      <div
        className="ccm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ccm-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="ccm__head">
          <div>
            <div className="ccm__title" id="ccm-title">Conference Categories</div>
            <div className="ccm__sub">Names and colours apply to every student's notes.</div>
          </div>
          <button type="button" className="ccm__close" onClick={onClose} aria-label="Close">
            <FiX size={18} />
          </button>
        </div>

        <div className="ccm__body">
          <form className="ccm__add" onSubmit={handleAdd}>
            <div className="ccm__add-row">
              <input
                type="text"
                className="ccm__input"
                placeholder="New category name…"
                value={newName}
                maxLength={40}
                onChange={(e) => setNewName(e.target.value)}
                disabled={busy}
              />
              <button
                type="submit"
                className="sdw-btn sdw-btn--primary sdw-btn--sm"
                disabled={busy || !normalizeCategoryName(newName)}
              >
                Add
              </button>
            </div>
            <ColorField value={newColor} onChange={setNewColor} disabled={busy} />
          </form>

          {(error || loadError) && (
            <div className="ccm__error" role="alert">{error || loadError}</div>
          )}

          <div className="ccm__list">
            {rows.length === 0 && <div className="ccm__empty">No categories yet.</div>}
            {rows.map((c) => {
              const isEditing = editingId === c.id;
              return (
                <div key={c.id} className="ccm__row">
                  {isEditing ? (
                    <>
                      <div className="ccm__row-main">
                        <input
                          type="text"
                          className="ccm__input"
                          value={editName}
                          maxLength={40}
                          autoFocus
                          disabled={busy}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              saveEdit(c);
                            }
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                        <ColorField value={editColor} onChange={setEditColor} disabled={busy} />
                      </div>
                      <div className="ccm__row-actions">
                        <button
                          type="button"
                          className="ccm__icon-btn ccm__icon-btn--save"
                          title="Save"
                          onClick={() => saveEdit(c)}
                          disabled={busy}
                        >
                          <FiCheck size={15} />
                        </button>
                        <button
                          type="button"
                          className="ccm__icon-btn"
                          title="Cancel"
                          onClick={() => setEditingId(null)}
                          disabled={busy}
                        >
                          <FiX size={15} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ccm__row-main">
                        <span
                          className="conf-cat-chip"
                          style={{
                            color: normalizeCategoryColor(c.color),
                            borderColor: normalizeCategoryColor(c.color),
                            background: `${normalizeCategoryColor(c.color)}1a`,
                          }}
                        >
                          {c.name}
                        </span>
                      </div>
                      <div className="ccm__row-actions">
                        <button
                          type="button"
                          className="ccm__icon-btn"
                          title="Edit name & colour"
                          onClick={() => startEdit(c)}
                          disabled={busy}
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          type="button"
                          className="ccm__icon-btn ccm__icon-btn--danger"
                          title="Delete"
                          onClick={() => handleDelete(c)}
                          disabled={busy}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
